import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useData } from '../../hooks/useData';
import { useVendorQuotes } from '../../hooks/useVendorQuotes';
import { TABLES } from '../../constants';
import { MODAL_TYPES } from '../../constants/modalTypes';
import { formatCurrency, formatPhoneNumber, validateEmail } from '../../utils/formatters';
import { Plus, MagnifyingGlass } from '@phosphor-icons/react';
import QuoteRow from './QuoteRow';
import pageStyles from '../../styles/pages.module.css';
import tableStyles from '../../styles/tables.module.css';
import styles from './VendorsPage.module.css';

/**
 * VendorsPage - Vendor directory + quote management
 *
 * Two tabs:
 *   1. ספריית ספקים (Vendor Directory) - table of all vendors with contact info
 *   2. הצעות מחיר (Quotes) - quote cards grouped by trade type with status management
 *
 * Reuses: useVendorQuotes, useVendorCreate, VendorFormModal, QuoteFormModal
 */

// Inline editable text cell — local state + save on blur (see FRONTEND.md)
const EditableTextCell = ({ value, onSave, placeholder }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    if (localValue !== value) {
      onSave(localValue);
    }
  };

  return (
    <input
      type="text"
      value={localValue || ''}
      onChange={e => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={tableStyles.inlineInputGhost}
    />
  );
};

// Inline editable phone cell — formats on blur using formatPhoneNumber
const EditablePhoneCell = ({ value, onSave, placeholder }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const trimmed = (localValue || '').trim();
    // Format the phone number before saving
    const formatted = trimmed ? formatPhoneNumber(trimmed) : '';
    if (formatted !== value) {
      onSave(formatted);
    }
    setLocalValue(formatted);
  };

  return (
    <input
      type="tel"
      value={localValue || ''}
      onChange={e => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={tableStyles.inlineInputGhost}
      dir="ltr"
    />
  );
};

// Inline editable email cell — validates on blur, reverts on invalid
const EditableEmailCell = ({ value, onSave, onError, placeholder }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const trimmed = (localValue || '').trim();
    if (trimmed && !validateEmail(trimmed)) {
      // Invalid email — revert and notify
      setLocalValue(value);
      if (onError) onError('כתובת אימייל לא תקינה');
      return;
    }
    if (trimmed !== value) {
      onSave(trimmed);
    }
  };

  return (
    <input
      type="email"
      value={localValue || ''}
      onChange={e => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={tableStyles.inlineInputGhost}
      dir="ltr"
    />
  );
};

const VendorsPage = () => {
  const { currentProject, openModal, showSuccess, showError } = useApp();
  const projectId = currentProject?.id;

  // UI state
  const [activeTab, setActiveTab] = useState('directory'); // 'directory' or 'quotes'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuoteId, setExpandedQuoteId] = useState(null);

  // ------------------------------------------------------------------
  // Data fetching — reuse existing hooks, no duplication
  // ------------------------------------------------------------------

  // Vendor quotes hook — provides quotes CRUD, share links, select/unselect
  const {
    quotes,
    quoteItems,
    isLoading: quotesLoading,
    updateQuote,
    removeQuote,
    generateShareLink,
    selectQuote,
  } = useVendorQuotes(projectId);

  // Master vendors for directory table
  const {
    data: masterVendors,
    isLoading: vendorsLoading,
    update: updateVendor,
  } = useData(TABLES.MASTER_VENDORS, {
    select: 'id, name, vendor_type_id, contact_person, contact_phone, contact_email',
    enabled: !!projectId,
  });

  // Vendor types reference (for display names)
  const { data: vendorTypesRef } = useData(TABLES.REFERENCE_VENDOR_TYPES, {
    enabled: !!projectId,
  });

  const isLoading = quotesLoading || vendorsLoading;

  // ------------------------------------------------------------------
  // Lookup maps
  // ------------------------------------------------------------------

  const vendorTypesMap = useMemo(() => {
    if (!vendorTypesRef) return new Map();
    return new Map(vendorTypesRef.map(vt => [vt.id, vt]));
  }, [vendorTypesRef]);

  const masterVendorsMap = useMemo(() => {
    if (!masterVendors) return new Map();
    return new Map(masterVendors.map(v => [v.id, v]));
  }, [masterVendors]);

  // ------------------------------------------------------------------
  // Directory tab: filtered & sorted vendor list
  // ------------------------------------------------------------------

  const filteredVendors = useMemo(() => {
    if (!masterVendors) return [];
    let list = [...masterVendors];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        v =>
          v.name?.toLowerCase().includes(q) ||
          v.contact_person?.toLowerCase().includes(q) ||
          v.contact_phone?.includes(q) ||
          v.contact_email?.toLowerCase().includes(q),
      );
    }

    // Sort alphabetically by name (Hebrew)
    list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'));
    return list;
  }, [masterVendors, searchQuery]);

  // ------------------------------------------------------------------
  // Quotes tab: quotes grouped by trade type
  // ------------------------------------------------------------------

  // Calculate total price per quote from its line items
  const quoteTotals = useMemo(() => {
    if (!quoteItems) return new Map();
    const totals = new Map();
    quoteItems.forEach(qi => {
      if (qi.line_price != null) {
        const current = totals.get(qi.quote_id) || 0;
        totals.set(qi.quote_id, current + parseFloat(qi.line_price));
      }
    });
    return totals;
  }, [quoteItems]);

  // Group quotes by vendor's trade type
  const quotesGroupedByTrade = useMemo(() => {
    if (!quotes || !masterVendorsMap.size) return [];

    // Build groups: trade name -> quotes[]
    const groups = {};
    const noTradeKey = '__no_trade__';

    quotes.forEach(q => {
      const vendor = masterVendorsMap.get(q.master_vendor_id);
      const vendorType = vendor?.vendor_type_id ? vendorTypesMap.get(vendor.vendor_type_id) : null;
      const tradeKey = vendorType?.id || noTradeKey;
      const tradeName = vendorType?.name || 'ללא סיווג';

      if (!groups[tradeKey]) {
        groups[tradeKey] = {
          tradeKey,
          tradeName,
          category: vendorType?.category || '',
          quotes: [],
        };
      }
      groups[tradeKey].quotes.push(q);
    });

    // Sort groups by category then trade name
    return Object.values(groups).sort((a, b) => {
      if (a.category !== b.category) return a.category.localeCompare(b.category, 'he');
      return a.tradeName.localeCompare(b.tradeName, 'he');
    });
  }, [quotes, masterVendorsMap, vendorTypesMap]);

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------

  const handleStatusChange = useCallback(
    async (quoteId, newStatus) => {
      try {
        if (newStatus === 'selected') {
          await selectQuote(quoteId);
        } else {
          await updateQuote({ id: quoteId, data: { status: newStatus } });
          showSuccess('הסטטוס עודכן');
        }
      } catch {
        showError('שגיאה בעדכון סטטוס');
      }
    },
    [updateQuote, selectQuote, showSuccess, showError],
  );

  const handleShareQuote = useCallback(
    async quoteId => {
      try {
        const link = await generateShareLink(quoteId);
        await navigator.clipboard.writeText(link);
        showSuccess('הקישור הועתק');
      } catch {
        showError('שגיאה ביצירת קישור');
      }
    },
    [generateShareLink, showSuccess, showError],
  );

  const handleDeleteQuote = useCallback(
    async quoteId => {
      if (!window.confirm('למחוק את הצעת המחיר?')) return;
      try {
        await removeQuote(quoteId);
        setExpandedQuoteId(null);
        showSuccess('הצעת המחיר נמחקה');
      } catch {
        showError('שגיאה במחיקת הצעה');
      }
    },
    [removeQuote, showSuccess, showError],
  );

  const handleAddVendor = useCallback(() => {
    openModal(MODAL_TYPES.VENDOR_FORM, { mode: 'standalone' });
  }, [openModal]);

  // Inline edit handler — update a single field on a vendor
  const handleUpdateVendor = useCallback(
    async (id, field, value) => {
      try {
        await updateVendor({ id, data: { [field]: value } });
      } catch {
        showError('שגיאה בעדכון ספק');
      }
    },
    [updateVendor, showError],
  );

  const handleAddQuote = useCallback(() => {
    openModal(MODAL_TYPES.QUOTE_FORM, { projectId });
  }, [openModal, projectId]);

  // ------------------------------------------------------------------
  // Render helpers
  // ------------------------------------------------------------------

  /** Render the vendor directory table */
  const renderDirectoryTab = () => (
    <div>
      {/* Search bar */}
      <div className={styles.searchBar}>
        <div className={styles.searchInputWrapper}>
          <MagnifyingGlass size={18} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="חיפוש ספק..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <button className={pageStyles.primaryButton} onClick={handleAddVendor}>
          <Plus size={16} weight="bold" />
          הוסף ספק
        </button>
      </div>

      {/* Vendors table */}
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead>
            <tr className={tableStyles.headerRow}>
              <th className={tableStyles.headerCell}>שם</th>
              <th className={tableStyles.headerCell}>סוג</th>
              <th className={tableStyles.headerCell}>איש קשר</th>
              <th className={tableStyles.headerCell}>טלפון</th>
              <th className={tableStyles.headerCell}>אימייל</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.length === 0 ? (
              <tr className={tableStyles.row}>
                <td className={`${tableStyles.cell} ${styles.emptyRow}`} colSpan={5}>
                  {searchQuery ? 'לא נמצאו ספקים' : 'אין ספקים עדיין'}
                </td>
              </tr>
            ) : (
              filteredVendors.map(vendor => {
                return (
                  <tr key={vendor.id} className={tableStyles.row}>
                    <td className={tableStyles.cell}>
                      <EditableTextCell
                        value={vendor.name}
                        onSave={val => handleUpdateVendor(vendor.id, 'name', val)}
                        placeholder="שם ספק"
                      />
                    </td>
                    <td className={tableStyles.cell}>
                      <select
                        className={`${tableStyles.inlineSelectGhost} ${!vendor.vendor_type_id ? tableStyles.ghostEmpty : ''}`}
                        value={vendor.vendor_type_id || ''}
                        onChange={e =>
                          handleUpdateVendor(vendor.id, 'vendor_type_id', e.target.value || null)
                        }
                      >
                        <option value="">בחר סוג...</option>
                        {(vendorTypesRef || []).map(vt => (
                          <option key={vt.id} value={vt.id}>
                            {vt.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={tableStyles.cell}>
                      <EditableTextCell
                        value={vendor.contact_person}
                        onSave={val => handleUpdateVendor(vendor.id, 'contact_person', val)}
                        placeholder="איש קשר"
                      />
                    </td>
                    <td className={tableStyles.cell}>
                      <EditablePhoneCell
                        value={vendor.contact_phone}
                        onSave={val => handleUpdateVendor(vendor.id, 'contact_phone', val)}
                        placeholder="050-000-0000"
                      />
                    </td>
                    <td className={tableStyles.cell}>
                      <EditableEmailCell
                        value={vendor.contact_email}
                        onSave={val => handleUpdateVendor(vendor.id, 'contact_email', val)}
                        onError={showError}
                        placeholder="email@example.com"
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  /** Render a single quote card */
  const renderQuoteCard = quote => {
    const vendor = masterVendorsMap.get(quote.master_vendor_id);
    const total = quoteTotals.get(quote.id);
    const isExpanded = expandedQuoteId === quote.id;
    const qItems = quoteItems?.filter(qi => qi.quote_id === quote.id) || [];
    const pricedCount = qItems.filter(qi => qi.line_price != null).length;

    return (
      <div key={quote.id} className={styles.quoteCardWrapper}>
        <QuoteRow
          quote={quote}
          vendorName={vendor?.name}
          totalOverride={total != null ? total : null}
          showVATTotal={true}
          priced={{ pricedCount, totalItems: qItems.length }}
          selected={isExpanded}
          onClick={() => setExpandedQuoteId(isExpanded ? null : quote.id)}
          onShare={() => handleShareQuote(quote.id)}
          onStatusChange={newStatus => handleStatusChange(quote.id, newStatus)}
          onDelete={quote.status === 'draft' ? () => handleDeleteQuote(quote.id) : undefined}
        />

        {/* Expanded detail — line items only (QuoteRow handles header actions) */}
        {isExpanded && <div className={styles.quoteDetail}>{renderQuoteLineItems(quote.id)}</div>}
      </div>
    );
  };

  /** Render line items for an expanded quote */
  const renderQuoteLineItems = quoteId => {
    const items = quoteItems?.filter(qi => qi.quote_id === quoteId) || [];
    if (items.length === 0) {
      return <p className={styles.noItemsText}>אין פריטים בהצעה זו</p>;
    }

    return (
      <table className={tableStyles.table}>
        <thead>
          <tr className={tableStyles.headerRow}>
            <th className={tableStyles.headerCell}>פריט</th>
            <th className={tableStyles.headerCell}>מחיר</th>
            <th className={tableStyles.headerCell}>הערות</th>
          </tr>
        </thead>
        <tbody>
          {items.map(qi => (
            <tr key={qi.id} className={tableStyles.row}>
              <td className={tableStyles.cell}>
                {qi.display_data?.name ||
                  qi.display_data?.description ||
                  `שורה ${qi.id.slice(0, 6)}`}
              </td>
              <td className={tableStyles.cell}>
                {qi.line_price != null ? formatCurrency(qi.line_price) : '—'}
              </td>
              <td className={`${tableStyles.cell} ${tableStyles.cellSecondary}`}>
                {qi.line_notes || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  /** Render the quotes tab */
  const renderQuotesTab = () => (
    <div>
      {/* Actions bar */}
      <div className={styles.searchBar}>
        <button className={pageStyles.primaryButton} onClick={handleAddQuote}>
          <Plus size={16} weight="bold" />
          הוסף הצעת מחיר
        </button>
      </div>

      {/* Quotes grouped by trade */}
      <div className={styles.quoteCardsGrid}>
        {quotesGroupedByTrade.length === 0 ? (
          <div className={pageStyles.emptyState}>אין הצעות מחיר עדיין</div>
        ) : (
          quotesGroupedByTrade.map(group => (
            <div key={group.tradeKey} className={styles.tradeGroup}>
              <div className={styles.tradeGroupHeader}>
                <h3 className={styles.tradeGroupTitle}>{group.tradeName}</h3>
                <span className={styles.tradeGroupCount}>{group.quotes.length} הצעות</span>
              </div>
              <div className={styles.tradeGroupCards}>
                {group.quotes.map(q => renderQuoteCard(q))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loadingContainer}>
          <div className={tableStyles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={pageStyles.pageContainer} dir="rtl">
      {/* Toolbar: tabs + page title */}
      <div className={pageStyles.toolbar}>
        <div className={pageStyles.tabBar}>
          <button
            className={activeTab === 'directory' ? pageStyles.tabActive : pageStyles.tab}
            onClick={() => setActiveTab('directory')}
          >
            ספריית ספקים
          </button>
          <button
            className={activeTab === 'quotes' ? pageStyles.tabActive : pageStyles.tab}
            onClick={() => setActiveTab('quotes')}
          >
            הצעות מחיר
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className={pageStyles.tabContent}>
        <div className={pageStyles.tabContentInner}>
          {activeTab === 'directory' && renderDirectoryTab()}
          {activeTab === 'quotes' && renderQuotesTab()}
        </div>
      </div>
    </div>
  );
};

export default VendorsPage;
